import { Any } from 'cosmjs-types/google/protobuf/any'

import { CoinAmount, CommissionRates, ValidatorDescription } from '@/types'

import { encodeCoin, encodeInt64, encodeMessage, encodeString } from './proto'

export interface DelegateNftParams {
  creator: string
  address: string
  validator: string
  amount: CoinAmount
}

export interface UndelegateNftParams {
  creator: string
  address: string
  validator: string
  share: string
}

export interface MintNftParams {
  creator: string
  owner: string
  startVestingBlock: bigint
  metadataCid: string
}

export interface BecameValidatorParams {
  delegator_address: string
  validator_address: string
  pubkey: Any
  nft_addresses: string[]
  description: ValidatorDescription
  commission?: CommissionRates
  min_self_delegation?: string
}

export function buildDelegateNftMsg(params: DelegateNftParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.creator),
    ...encodeString(2, params.address),
    ...encodeString(3, params.validator),
    ...encodeMessage(4, encodeCoin(params.amount.denom, params.amount.amount)),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.nft.MsgDelegate',
    value,
  })
}

export function buildUndelegateNftMsg(params: UndelegateNftParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.creator),
    ...encodeString(2, params.address),
    ...encodeString(3, params.validator),
    ...encodeString(4, params.share),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.nft.MsgUndelegate',
    value,
  })
}

export function buildMintNftMsg(params: MintNftParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.creator),
    ...encodeString(2, params.owner),
    ...encodeInt64(3, params.startVestingBlock),
    ...encodeString(4, params.metadataCid),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.nft.MsgMint',
    value,
  })
}

export interface WithdrawalParams {
  creator: string
  address: string
  amount: CoinAmount
}

export function buildWithdrawalMsg(params: WithdrawalParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.creator),
    ...encodeString(2, params.address),
    ...encodeMessage(3, encodeCoin(params.amount.denom, params.amount.amount)),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.nft.MsgWithdrawal',
    value,
  })
}

export interface TopUpParams {
  creator: string
  address: string
  amount: CoinAmount
}

export function buildTopUpMsg(params: TopUpParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.creator),
    ...encodeString(2, params.address),
    ...encodeMessage(3, encodeCoin(params.amount.denom, params.amount.amount)),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.nft.MsgTopUp',
    value,
  })
}

export interface CloseNftParams {
  creator: string
  address: string
}

export function buildCloseNftMsg(params: CloseNftParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.creator),
    ...encodeString(2, params.address),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.nft.MsgClose',
    value,
  })
}

export interface RedelegateParams {
  creator: string
  address: string
  validatorSrc: string
  validatorNew: string
  share: string
}

export function buildRedelegateNftMsg(params: RedelegateParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.creator),
    ...encodeString(2, params.address),
    ...encodeString(3, params.validatorSrc),
    ...encodeString(4, params.validatorNew),
    ...encodeString(5, params.share),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.nft.MsgRedelegate',
    value,
  })
}

export interface ChangeOwnerParams {
  creator: string
  address: string
  recipient: string
  amount: CoinAmount
}

export function buildChangeOwnerMsg(params: ChangeOwnerParams): Any {
  const value = Uint8Array.from([
    ...encodeString(1, params.creator),
    ...encodeString(2, params.address),
    ...encodeString(3, params.recipient),
    ...encodeMessage(4, encodeCoin(params.amount.denom, params.amount.amount)),
  ])

  return Any.fromPartial({
    typeUrl: '/cosmos.nft.MsgSend',
    value,
  })
}

function encodeDescriptionMsg(desc: ValidatorDescription): Uint8Array {
  const parts: number[] = []
  parts.push(...encodeString(1, desc.moniker))
  if (desc.identity) parts.push(...encodeString(2, desc.identity))
  if (desc.website) parts.push(...encodeString(3, desc.website))
  if (desc.security_contact)
    parts.push(...encodeString(4, desc.security_contact))
  if (desc.details) parts.push(...encodeString(5, desc.details))
  return Uint8Array.from(parts)
}

function encodeCommissionRatesMsg(rates: CommissionRates): Uint8Array {
  return Uint8Array.from([
    ...encodeString(1, rates.rate),
    ...encodeString(2, rates.max_rate),
    ...encodeString(3, rates.max_change_rate),
  ])
}

function encodeAnyMessage(msg: Any): Uint8Array {
  const typeUrl = msg.typeUrl ?? ''
  const valueBytes = msg.value ?? new Uint8Array()
  return Uint8Array.from([
    ...encodeString(1, typeUrl),
    ...encodeMessage(2, valueBytes),
  ])
}

const DEFAULT_COMISSION_RATES = {
  rate: '100000000000000000',
  max_rate: '200000000000000000',
  max_change_rate: '10000000000000000',
}

const MIN_SELF_DELEGATION = '1'

export function buildBecameValidatorMsg(params: BecameValidatorParams): Any {
  const commission: CommissionRates =
    params.commission ?? DEFAULT_COMISSION_RATES
  const minSelfDelegation = params.min_self_delegation ?? MIN_SELF_DELEGATION

  const descriptionBytes = encodeDescriptionMsg(params.description)
  const commissionBytes = encodeCommissionRatesMsg(commission)
  const pubkeyBytes = encodeAnyMessage(params.pubkey)

  const parts: number[] = []
  parts.push(...encodeMessage(1, descriptionBytes))
  parts.push(...encodeMessage(2, commissionBytes))
  parts.push(...encodeString(3, minSelfDelegation))
  parts.push(...encodeString(4, params.delegator_address))
  parts.push(...encodeString(5, params.validator_address))
  parts.push(...encodeMessage(6, pubkeyBytes))
  for (const nftAddress of params.nft_addresses) {
    parts.push(...encodeString(7, nftAddress))
  }

  const value = Uint8Array.from(parts)
  return Any.fromPartial({
    typeUrl: '/cosmos.nft.MsgBecameValidator',
    value,
  })
}
